/** @format */
import {
    Attainment,
    CourseUnit,
    CourseUnitAttainment,
    CourseUnitSelection,
    CustomCourseUnitAttainmentSelection,
    CustomModuleAttainmentSelection,
    CustomStudyDraft,
    DegreeProgrammeAttainment,
    LocalizedString,
    Module,
    ModuleAttainment,
    ModuleSelection,
} from 'common-typescript/types';

export interface AttainmentsById {
    [index: string]: Attainment;
}

export interface IndexedAttainments {
    byId: AttainmentsById;
    byCourseUnitGroupId: { [index: string]: CourseUnitAttainment };
    byModuleGroupId: { [index: string]: ModuleAttainment | DegreeProgrammeAttainment };
    byCustomStudyDraftId: { [index: string]: Attainment };
}

export interface Ordering {
    ordering?: number;
}

export interface ModulesById {
    [index: string]: Module;
}

export interface CourseUnitsById {
    [index: string]: CourseUnit;
}

export type CourseUnitSelectionWithOrdering = CourseUnitSelection & { ordering?: number; substitutedByOrdering?: number[] };
export type ModuleSelectionWithOrdering = ModuleSelection &
    Ordering & { hidden?: boolean; moduleNotAttained?: boolean; newName?: LocalizedString };
export type CustomCourseUnitAttainmentSelectionWithOrdering = CustomCourseUnitAttainmentSelection & Ordering;
export type CustomModuleAttainmentSelectionWithOrdering = CustomModuleAttainmentSelection & Ordering;
export type CustomStudyDraftWithOrdering = CustomStudyDraft & Ordering;
export type StudySelection =
    | CourseUnitSelectionWithOrdering
    | ModuleSelectionWithOrdering
    | CustomCourseUnitAttainmentSelectionWithOrdering
    | CustomModuleAttainmentSelectionWithOrdering
    | CustomStudyDraftWithOrdering;

export interface PlanWithOrdering {
    courseUnitSelections: CourseUnitSelectionWithOrdering[];
    customCourseUnitAttainmentSelections: CustomCourseUnitAttainmentSelectionWithOrdering[];
    customStudyDrafts: CustomStudyDraftWithOrdering[];
    moduleSelections: ModuleSelectionWithOrdering[];
    customModuleAttainmentSelections: CustomModuleAttainmentSelectionWithOrdering[];
}

export interface SelectionItem {
    type: 'GroupingModuleLike' | 'Attainment';
    studySelection: StudySelection;
    ordering: number;
    attainment?: Attainment;
}

export function isModuleSelection(studySelection: StudySelection): studySelection is ModuleSelectionWithOrdering {
    return !!(<ModuleSelectionWithOrdering>studySelection)?.moduleId;
}

export function isCourseUnitSelection(studySelection: StudySelection): studySelection is CourseUnitSelectionWithOrdering {
    return !!(<CourseUnitSelectionWithOrdering>studySelection)?.courseUnitId;
}

export function isCustomModuleSelection(studySelection: StudySelection): studySelection is CustomModuleAttainmentSelectionWithOrdering {
    return !!(<CustomModuleAttainmentSelectionWithOrdering>studySelection)?.customModuleAttainmentId;
}

export function isCustomCourseUnitSelection(
    studySelection: StudySelection,
): studySelection is CustomCourseUnitAttainmentSelectionWithOrdering {
    return !!(<CustomCourseUnitAttainmentSelectionWithOrdering>studySelection)?.customCourseUnitAttainmentId;
}

export function isCustomStudyDraftSelection(studySelection: StudySelection): studySelection is CustomStudyDraftWithOrdering {
    return !!(<CustomStudyDraftWithOrdering>studySelection)?.id;
}

export interface CollapsedByGroupingModuleId {
    [index: string]: boolean;
}
